import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SearchIcon from '@mui/icons-material/Search';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';

import {
  EmptyText,
  LoadingBox,
  MemberAvatar,
  MemberInfoBox,
  MemberMenuItem,
  MemberMenuList,
  PendingSpinner,
  PickerPaper,
  PickerPopper,
  PickerTriggerButton,
  SearchBox,
  SectionLabel,
  SectionLabelBox,
} from './MemberPickerDropdown.styles';

export interface MemberPickerItem {
  userId: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

export interface MemberPickerDropdownProps {
  /** List of members to display in the picker */
  members: MemberPickerItem[];
  /** Currently selected user IDs */
  selectedIds: Set<string>;
  /** Called when a member row is clicked */
  onToggle: (member: MemberPickerItem) => void;
  /** Returns true while a mutation is in-flight for a given userId */
  isBusy?: (userId: string) => boolean;
  /** Whether the member list is being fetched */
  isLoading?: boolean;
  /** Label for the trigger button */
  buttonLabel?: string;
  /** Section heading shown above the list */
  listLabel?: string;
  /** Placement of the dropdown relative to the trigger */
  placement?: 'bottom-start' | 'bottom-end' | 'bottom';
  /** Called when the dropdown is opened (useful for triggering a query) */
  onOpen?: () => void;
}

const MemberPickerDropdown: React.FC<MemberPickerDropdownProps> = ({
  members,
  selectedIds,
  onToggle,
  isBusy = () => false,
  isLoading = false,
  buttonLabel = 'Add members',
  listLabel = 'USERS',
  placement = 'bottom-start',
  onOpen,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const handleOpen = () => {
    setOpen((prev) => {
      if (!prev) onOpen?.();
      return !prev;
    });
  };

  const filtered = members.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <PickerTriggerButton ref={anchorRef} startIcon={<PersonAddAlt1Icon />} onClick={handleOpen}>
        {buttonLabel}
      </PickerTriggerButton>

      <PickerPopper open={open} anchorEl={anchorRef.current} placement={placement}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <PickerPaper elevation={4}>
            {/* Search */}
            <SearchBox>
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Search users or groups"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </SearchBox>

            <Divider />

            {/* Section label */}
            <SectionLabelBox>
              <SectionLabel variant="caption">{listLabel}</SectionLabel>
            </SectionLabelBox>

            {/* Member list */}
            {isLoading ? (
              <LoadingBox>
                <PendingSpinner size={22} color="secondary" />
              </LoadingBox>
            ) : filtered.length === 0 ? (
              <EmptyText variant="body2" color="text.secondary">
                No users found
              </EmptyText>
            ) : (
              <MemberMenuList dense>
                {filtered.map((member) => {
                  const checked = selectedIds.has(member.userId);
                  const pending = isBusy(member.userId);
                  return (
                    <MemberMenuItem
                      key={member.userId}
                      onClick={() => !pending && onToggle(member)}
                    >
                      {pending ? (
                        <PendingSpinner size={18} color="secondary" />
                      ) : (
                        <Checkbox
                          size="small"
                          checked={checked}
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          tabIndex={-1}
                          disableRipple
                          color="secondary"
                        />
                      )}
                      <MemberAvatar src={member.avatarUrl} alt={member.name}>
                        {member.name.charAt(0).toUpperCase()}
                      </MemberAvatar>
                      <MemberInfoBox>
                        <Typography variant="body2" noWrap>
                          {member.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {member.email}
                        </Typography>
                      </MemberInfoBox>
                    </MemberMenuItem>
                  );
                })}
              </MemberMenuList>
            )}
          </PickerPaper>
        </ClickAwayListener>
      </PickerPopper>
    </>
  );
};

export default MemberPickerDropdown;
